import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import MobileScanInput from '../components/MobileScanInput';
import {
  Truck,
  Package,
  CheckCircle2,
  ChevronRight,
  RotateCcw,
  MapPin,
  Clock,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { useMobile } from '../context/MobileContext';
import { wmsEvents } from '@/shared/services/trailService';
import { dispatchAPI } from '@/shared/services/api';

const STEPS = {
  SELECT_ROUTE: 'select_route',
  SCAN_ITEMS: 'scan_items',
  CONFIRM_LOAD: 'confirm_load',
  COMPLETE: 'complete'
};

const MobileLoadout = () => {
  const navigate = useNavigate();
  const { setCurrentTask } = useMobile();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_ROUTE);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loadItems, setLoadItems] = useState([]);
  const [scannedItems, setScannedItems] = useState([]);

  // Dynamic state for routes
  const [routes, setRoutes] = useState([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);

  useEffect(() => {
    setCurrentTask({
      name: 'Load-out',
      href: '/mobile/loadout',
      description: 'Load cartons to routes'
    });
    fetchRoutes();
    return () => setCurrentTask(null);
  }, [setCurrentTask]);

  const fetchRoutes = async () => {
    setIsLoadingRoutes(true);
    try {
      const data = await dispatchAPI.getDispatches({ status: 'scheduled' });
      setRoutes(Array.isArray(data) ? data : data.dispatches || []);
    } catch (err) {
      toast.error('Failed to fetch scheduled routes');
    } finally {
      setIsLoadingRoutes(false);
    }
  };

  const totalItems = loadItems.length;
  const scannedCount = scannedItems.length;
  const progress = totalItems > 0 ? (scannedCount / totalItems) * 100 : 0;

  const handleSelectRoute = async (route) => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    try {
      const data = await dispatchAPI.startLoading(route.id, 'DOCK-1');

      setSelectedRoute(route);
      setLoadItems(data.items || []);
      setCurrentStep(STEPS.SCAN_ITEMS);

      // Emit trail event for route selected
      wmsEvents.loadoutRouteSelected(route.id, userId);

      toast.success('Route Selected', { description: `Loading ${route.id} - ${data.items?.length || 0} items` });
    } catch (err) {
      toast.error(err.message || 'Failed to start loading');
    }
  };

  const handleItemScanSuccess = async (barcode) => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    const itemIndex = loadItems.findIndex(item =>
      item.id === barcode || barcode.includes(item.id) || (item.orderId && barcode.includes(item.orderId))
    );

    if (itemIndex >= 0) {
      const item = loadItems[itemIndex];

      if (scannedItems.some(s => s.id === item.id)) {
        toast.warning('Already Scanned', { description: `${item.id} is already loaded` });
        return;
      }

      try {
        await dispatchAPI.loadItem(selectedRoute.id, item.id);

        // Mark as scanned locally
        setLoadItems(prev => prev.map((i, idx) =>
          idx === itemIndex ? { ...i, scanned: true } : i
        ));
        setScannedItems(prev => [...prev, item]);

        // Emit trail event for item loaded
        wmsEvents.loadoutItemScanned(selectedRoute.id, item.id, item.type, userId);

        const newScannedCount = scannedCount + 1;

        if (newScannedCount === totalItems) {
          toast.success('All Items Loaded!', { description: 'Ready to confirm and dispatch' });
          setCurrentStep(STEPS.CONFIRM_LOAD);
        } else {
          toast.success('Item Loaded', { description: `${totalItems - newScannedCount} remaining` });
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load item');
      }
    } else {
      toast.error('Wrong Route', { description: 'This item is not assigned to this route' });
    }
  };

  const handleConfirmLoad = async () => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';
    const departureTime = new Date().toISOString();

    try {
      await dispatchAPI.confirmDeparture({
        dispatch_id: selectedRoute.id,
        actual_departure: departureTime
      });

      // Emit trail event for route loaded
      wmsEvents.loadoutRouteLoaded(selectedRoute.id, scannedItems.length, userId);

      setCurrentStep(STEPS.COMPLETE);
      setRoutes(prev => prev.filter(r => r.id !== selectedRoute.id));
      toast.success('Route Loaded', { description: `${selectedRoute.id} ready for dispatch` });
    } catch (err) {
      toast.error(err.message || 'Failed to confirm departure');
    }
  };

  const handleForceComplete = () => {
    if (scannedCount < totalItems) {
      const remaining = totalItems - scannedCount;
      toast.warning('Incomplete Load', {
        description: `${remaining} items not scanned. Proceeding anyway.`
      });
    }
    setCurrentStep(STEPS.CONFIRM_LOAD);
  };

  const startNewRoute = () => {
    setCurrentStep(STEPS.SELECT_ROUTE);
    setSelectedRoute(null);
    setLoadItems([]);
    setScannedItems([]);
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SELECT_ROUTE:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Routes to Load</h2>
              <Badge>{routes.length} routes</Badge>
            </div>

            {routes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="font-semibold text-lg">All Routes Loaded!</h3>
                  <p className="text-muted-foreground">No pending routes</p>
                </CardContent>
              </Card>
            ) : (
              routes.map((route) => (
                <Card
                  key={route.id}
                  className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
                  onClick={() => handleSelectRoute(route)}
                  data-testid={`route-${route.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-red-500 text-white">
                        <Truck className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{route.id}</h3>
                          <Badge variant="outline">{route.packages} pkg</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{route.driver}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {route.stops} stops
                          </span>
                          <span className="text-xs flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Depart {route.departure}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">{route.zone}</Badge>
                        <ChevronRight className="h-5 w-5 text-muted-foreground mt-2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        );

      case STEPS.SCAN_ITEMS:
        return (
          <div className="space-y-4">
            {/* Progress Header */}
            <Card className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm opacity-80">{selectedRoute.id}</p>
                    <p className="text-2xl font-bold">{scannedCount} / {totalItems}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-80">Departure</p>
                    <p className="text-xl font-bold">{selectedRoute.departure}</p>
                  </div>
                </div>
                <Progress value={progress} className="h-2 bg-white/20" />
              </CardContent>
            </Card>

            {/* Route Info */}
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-red-600" />
                    <span>{selectedRoute.vehicle}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>{selectedRoute.driver}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scanner */}
            <MobileScanInput
              title="Scan Package/Pallet"
              subtitle={`Loading ${selectedRoute.id}`}
              gateId="GATE_LOAD_ITEM"
              barcodeType="CARTON"
              scanContext="LOADING"
              onScanSuccess={handleItemScanSuccess}
              chainMapping={{ route_id: selectedRoute.id }}
              validateScan={async (barcode) => {
                const item = loadItems.find(i =>
                  i.id === barcode || barcode.includes(i.id) || barcode.includes(i.orderId)
                );
                if (!item) return { success: false, result: 'REJECTED', message: 'Not on this route' };
                if (scannedItems.some(s => s.id === item.id)) {
                  return { success: false, result: 'WARNING', message: 'Already loaded' };
                }
                return { success: true, result: 'OK' };
              }}
            />

            {/* Load List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Load Manifest</span>
                  <Badge variant={scannedCount === totalItems ? 'default' : 'outline'}>
                    {scannedCount}/{totalItems}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-40 overflow-y-auto">
                {loadItems.map((item) => {
                  const isScanned = scannedItems.some(s => s.id === item.id);
                  return (
                    <div key={item.id} className="px-4 py-2 border-b last:border-0 flex items-center gap-3">
                      {isScanned ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono truncate">{item.id}</p>
                        <p className="text-xs text-muted-foreground">{item.stop} • {item.type}</p>
                      </div>
                      <Badge variant={isScanned ? 'default' : 'outline'} className="text-xs">
                        {item.weight}kg
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={startNewRoute} className="flex-1">
                Cancel
              </Button>
              {scannedCount > 0 && scannedCount < totalItems && (
                <Button
                  variant="outline"
                  onClick={handleForceComplete}
                  className="flex-1 gap-2 text-amber-600 border-amber-300"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Complete ({scannedCount})
                </Button>
              )}
              {scannedCount === totalItems && (
                <Button onClick={() => setCurrentStep(STEPS.CONFIRM_LOAD)} className="flex-1">
                  Confirm Load
                </Button>
              )}
            </div>
          </div>
        );

      case STEPS.CONFIRM_LOAD:
        return (
          <div className="space-y-6 py-4">
            <Card className="border-2 border-red-300">
              <CardContent className="p-6 text-center">
                <Truck className="h-12 w-12 mx-auto text-red-600 mb-4" />
                <h2 className="text-xl font-bold mb-2">Confirm Route Load</h2>
                <p className="text-muted-foreground mb-4">
                  Ready to dispatch {selectedRoute.id}
                </p>

                <div className="space-y-2 text-left bg-muted/50 rounded-lg p-4 mb-4">
                  <p className="text-sm"><strong>Route:</strong> {selectedRoute.id}</p>
                  <p className="text-sm"><strong>Driver:</strong> {selectedRoute.driver}</p>
                  <p className="text-sm"><strong>Vehicle:</strong> {selectedRoute.vehicle}</p>
                  <p className="text-sm"><strong>Stops:</strong> {selectedRoute.stops}</p>
                  <p className="text-sm"><strong>Packages:</strong> {scannedCount} of {totalItems}</p>
                  <p className="text-sm"><strong>Departure:</strong> {selectedRoute.departure}</p>
                </div>

                {scannedCount < totalItems && (
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {totalItems - scannedCount} items not scanned
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button onClick={handleConfirmLoad} className="w-full gap-2" size="lg">
              <FileText className="h-5 w-5" />
              Generate Manifest & Dispatch
            </Button>

            <Button variant="outline" onClick={() => setCurrentStep(STEPS.SCAN_ITEMS)} className="w-full">
              Back to Loading
            </Button>
          </div>
        );

      case STEPS.COMPLETE:
        return (
          <div className="space-y-6 text-center py-8">
            <div className="mx-auto w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Load-out Complete!</h2>
              <p className="text-muted-foreground mt-2">{selectedRoute.id} dispatched</p>
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xl font-bold">{scannedCount}</p>
                <p className="text-xs text-muted-foreground">Packages</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xl font-bold">{selectedRoute.stops}</p>
                <p className="text-xs text-muted-foreground">Stops</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xl font-bold">{selectedRoute.departure}</p>
                <p className="text-xs text-muted-foreground">Depart</p>
              </div>
            </div>
            <div className="flex gap-3 max-w-sm mx-auto">
              <Button variant="outline" onClick={startNewRoute} className="flex-1">
                <RotateCcw className="h-4 w-4 mr-2" />
                Next Route
              </Button>
              <Button onClick={() => navigate('/mobile')} className="flex-1">
                Done
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4" data-testid="mobile-loadout">
      {renderStep()}
    </div>
  );
};

export default MobileLoadout;
